import { formulaParser, simpleParser } from '../lezer-syntax-parser';
import { type FormulaOutputType } from '../models/formula-output-type';
import { temporaryFunctionRegex } from '../tokens';

import { getSimpleSyntaxType, SimpleSyntaxTypes } from './get-simple-syntax-type';
import { buildTokensTree, type CodeToken, getTokenByPath } from './syntax-tree-builder';

const convertNodeToSimpleSyntax = (node: CodeToken): string => {
  if (node.name === 'String') {
    return JSON.parse(node.text);
  }
  if (node.name === 'VariableName') {
    return node.text;
  }
  if (node.name === 'Number') {
    return node.text;
  }
  return node.text;
};

export const convertFormulaToSimpleSyntax = (formula: string | undefined): string | undefined => {
  if (formula === undefined) {
    return;
  }

  const type = getSimpleSyntaxType(formula);
  if (type === SimpleSyntaxTypes.unknown) {
    return formula;
  }

  const treeNodes = buildTokensTree(formula, formulaParser);

  if (type === SimpleSyntaxTypes.literal) {
    return treeNodes.text;
  }
  let ingredients: CodeToken[];

  if (type === SimpleSyntaxTypes.array) {
    ingredients = getTokenByPath(treeNodes, ['Expression', '*', '*', '*', '*', '*']);
  } else {
    ingredients = getTokenByPath(treeNodes, ['Expression', '*', '*']);
  }
  const mappedIngredients = ingredients.map(convertNodeToSimpleSyntax);

  return mappedIngredients.join('');
};
export const isNumeric = (value: string): boolean => {
  return !isNaN(parseFloat(value)) && isFinite(parseFloat(value)) && !Number.isNaN(+value);
};

export const convertFormulaBackToComplexSyntax = (
  formula: string,
  outputType: FormulaOutputType = 'String',
): string => {
  const treeNodes = buildTokensTree(formula, simpleParser);
  const tokens = getTokenByPath(treeNodes, ['UserInputSequence', '*', '*']);
  const hasVariables = tokens.some((t) => t.name === 'VariableName');
  const multipleTokens = tokens.length > 1;

  const outputIsArray = ['ArrayNumbers', 'ArrayStrings'].includes(outputType);
  const shouldUseArray = outputIsArray && multipleTokens;

  const mappedTokens = tokens.map((token) => {
    if (token.name === 'Literal') {
      if (isNumeric(token.text) && !['ArrayStrings', 'String'].includes(outputType)) {
        return token.text;
      }
      if (!hasVariables && !shouldUseArray) {
        return token.text;
      }
      return JSON.stringify(token.text);
    }
    if (token.name === 'VariableName') {
      if (temporaryFunctionRegex.test(token.text)) {
        return token.text.replace(/_#_/g, '');
      }
      return token.text;
    }
    return token.text;
  });

  if (shouldUseArray) {
    return `[${mappedTokens.join(',')}]`;
  }
  return mappedTokens.join('+');
};

export const canBeConvertedToSimpleSyntax = (formula: string): boolean => {
  return getSimpleSyntaxType(formula) !== SimpleSyntaxTypes.unknown;
};

export const isFormula = (formula: string | undefined | null): boolean => {
  if (!formula) {
    return false;
  }
  const tree = formulaParser.parse(formula);
  const cursor = tree.cursor();
  let isFormula = false;

  do {
    if (['Operator', 'FunctionCall'].includes(cursor.name)) {
      isFormula = true;
      break;
    }
  } while (cursor.next());

  return isFormula;
};
